import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { useNavigate } from 'react-router-dom';
import { Upload, Image as ImageIcon, Trash2, Eye, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import sliderService from '../services/sliderService';
import { uploadService, authService } from '../services/api';

function AdminSlider() {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null
  });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await sliderService.getAll();
      setSlides(response.data || []);
    } catch (error) {
      console.error('Error fetching slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setFormData({ ...formData, image: file });
        const reader = new FileReader();
        reader.onload = (e) => setPreviewImage(e.target.result);
        reader.readAsDataURL(file);
      } else {
        alert('لطفاً یک فایل تصویر انتخاب کنید');
      }
    }
  };

  const saveAll = async (updatedSlides) => {
    const reordered = updatedSlides.map((s, idx) => ({ ...s, order: idx + 1 }));
    await sliderService.saveAll(reordered);
    setSlides(reordered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = editingSlide ? editingSlide.image : '';
      if (formData.image instanceof File) {
        const uploadRes = await uploadService.uploadSlide(formData.image);
        imageUrl = uploadRes.data.url;
      }

      let updated;

      if (editingSlide) {
        const changed = { ...editingSlide, title: formData.title, description: formData.description, image: imageUrl };
        const response = await sliderService.update(editingSlide.id, changed);
        updated = slides.map(s => s.id === editingSlide.id ? response.data : s);
      } else {
        const newSlide = { title: formData.title, description: formData.description, image: imageUrl, order: slides.length + 1 };
        const response = await sliderService.create(newSlide);
        updated = [...slides, response.data];
      }

      await saveAll(updated);

      setFormData({ title: '', description: '', image: null });
      setPreviewImage(null);
      setEditingSlide(null);
      document.getElementById('image-upload').value = '';
    } catch (error) {
      console.error('Error saving slide:', error);
      alert('خطا در ذخیره اسلاید');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title,
      description: slide.description,
      image: null
    });
    setPreviewImage(slide.image);
  };

  const handleDelete = async (slideId) => {
    if (window.confirm('آیا از حذف این اسلاید مطمئن هستید؟')) {
      try {
        await sliderService.delete(slideId);
        await fetchSlides();
      } catch (error) {
        console.error('Error deleting slide:', error);
        alert('خطا در حذف اسلاید');
      }
    }
  };

  const moveSlide = async (slideId, direction) => {
    const newSlides = [...slides];
    const index = newSlides.findIndex(s => s.id === slideId);

    if (direction === 'up' && index > 0) {
      [newSlides[index], newSlides[index - 1]] = [newSlides[index - 1], newSlides[index]];
    } else if (direction === 'down' && index < newSlides.length - 1) {
      [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];
    }

    await saveAll(newSlides);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت اسلایدر</h1>
              <p className="text-xl text-blue-100">مدیریت تصاویر اسلایدر صفحه اصلی</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Add/Edit Slide Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {editingSlide ? 'ویرایش اسلاید' : 'افزودن اسلاید جدید'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  عنوان اسلاید
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="عنوان اسلاید را وارد کنید"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  توضیحات
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="توضیحات اسلاید را وارد کنید"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                تصویر اسلاید
              </label>
              <input
                id="image-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                className="w-full"
              />
              {previewImage && (
                <div className="mt-4">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="max-w-xs rounded-lg shadow-md"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={uploading}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition disabled:opacity-50"
              >
                <Upload size={20} />
                {uploading ? 'در حال ذخیره...' : (editingSlide ? 'بروزرسانی اسلاید' : 'افزودن اسلاید')}
              </button>
            </div>
          </form>
        </div>

        {/* Slides List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">اسلایدهای فعلی</h2>

          {slides.length === 0 ? (
            <p className="text-gray-500 text-center py-8">هیچ اسلایدی ثبت نشده است</p>
          ) : (
            <div className="space-y-4">
              {slides.map((slide, idx) => (
                <div key={slide.id} className="border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {slide.image ? (
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className="w-24 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-24 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        <ImageIcon className="text-gray-400" size={24} />
                      </div>
                    )}
                  </div>

                  <div className="flex-grow">
                    <h3 className="font-bold text-gray-900">{slide.title}</h3>
                    <p className="text-sm text-gray-600">{slide.description}</p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => moveSlide(slide.id, 'up')}
                      disabled={idx === 0}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                      <ChevronRight size={20} />
                    </button>
                    <button
                      onClick={() => moveSlide(slide.id, 'down')}
                      disabled={idx === slides.length - 1}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={() => handleEdit(slide)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Eye size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(slide.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSlider;
